'use client';

import * as React from 'react';
import { motion, useAnimation, type LegacyAnimationControls, type SVGMotionProps, type Variants } from 'motion/react';

type IconProps<T extends string = string> = Omit<SVGMotionProps<SVGSVGElement>, 'animate'> & {
  animate?: boolean;
  animateOnHover?: boolean;
  animation?: T;
  size?: number;
};

type IconWrapperProps<T extends string> = IconProps<T> & {
  icon: React.ComponentType<IconProps<T>>;
};

type AnimateIconContextValue = {
  controls: LegacyAnimationControls | undefined;
  animation: string;
};

const AnimateIconContext = React.createContext<AnimateIconContextValue>({
  controls: undefined,
  animation: 'default',
});

function useAnimateIconContext(): AnimateIconContextValue {
  return React.useContext(AnimateIconContext);
}

function IconWrapper<T extends string>({
  icon: IconComponent,
  size = 28,
  animate,
  animateOnHover = false,
  animation = 'default' as T,
  ...props
}: IconWrapperProps<T>): React.ReactElement {
  const controls = useAnimation();
  const [isHovered, setIsHovered] = React.useState(false);
  const isAnimating = animate ?? (animateOnHover && isHovered);

  React.useEffect(() => {
    void controls.start(isAnimating ? 'animate' : 'initial');
  }, [controls, isAnimating]);

  return (
    <motion.span
      onMouseEnter={animateOnHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={animateOnHover ? () => setIsHovered(false) : undefined}
      style={{ display: 'inline-flex', lineHeight: 0 }}
    >
      <AnimateIconContext.Provider value={{ controls, animation }}>
        <IconComponent size={size} {...props} />
      </AnimateIconContext.Provider>
    </motion.span>
  );
}

function getVariants<
  V extends { default: T; [key: string]: T },
  T extends Record<string, Variants>,
>(animations: V, animation: string): T {
  return (animations[animation as keyof V] ?? animations.default) as T;
}

export {
  IconWrapper,
  useAnimateIconContext,
  getVariants,
  type IconProps,
  type IconWrapperProps,
  type AnimateIconContextValue,
};
